import React, { useEffect, useState, useCallback } from "react";
import { Spin, Alert } from "antd";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from "recharts";
import customClientAxios from "../../customAxios";
import { PlatformAnalyticsRevenueChart } from "../../utils/ApiEndpoints";
import { PageContainer, Card, SectionTitle, CHART_COLORS, fmtRupees } from "../paUtils";
import styled from "styled-components";

const CustomTooltipBox = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px 16px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  font-size: 13px;
`;

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <CustomTooltipBox>
      <div style={{ fontWeight: 600, color: "#1e293b", marginBottom: 6 }}>{label}</div>
      {payload.map((entry) => (
        <div key={entry.dataKey} style={{ display: "flex", alignItems: "center", gap: 8, margin: "4px 0", color: "#374151" }}>
          <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: entry.color }} />
          <span style={{ color: "#64748b", minWidth: 100 }}>{entry.name}:</span>
          <span style={{ fontWeight: 600 }}>{fmtRupees(entry.value)}</span>
        </div>
      ))}
    </CustomTooltipBox>
  );
}

function RevenueTab({ startDate, endDate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await customClientAxios().get(PlatformAnalyticsRevenueChart, {
        params: { startDate, endDate, period: "monthly" },
      });
      setData(res.data.data);
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to load revenue data");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const chartData = data?.dataPoints || [];

  return (
    <PageContainer>
      {error && <Alert type="error" message={error} style={{ marginBottom: 20 }} />}

      <Spin spinning={loading}>
        <Card>
          <SectionTitle>Platform Fee Earned (Monthly)</SectionTitle>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94a3b8" }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} tickLine={false} axisLine={false} tickFormatter={(v) => fmtRupees(v)} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="platformFee" name="Platform Fee" fill={CHART_COLORS.platformFee} radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </Spin>
    </PageContainer>
  );
}

export default RevenueTab;
